import MedicalHistoryIcon from 'components/atoms/icons/medicalHistoryIcon';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import React, { useState } from 'react';
import { useAddMedicalHistory } from 'infrastructure/hooks/ccm';
import MedicalHistoryForm from 'components/molecules/admin-panel/forms/medical-history';
import { useTrackingPlatform } from 'infrastructure/hooks/patient/use-tracking-platform';
import { useCheckRoleAccess } from 'infrastructure/hooks';

import MedicalHistoryTable from './components/medical-table';
import s from './styles.module.scss';
import DraggableModal from '../draggableModal';

import type { ReactNode, FC } from 'react';
import type { Values } from 'components/molecules/admin-panel/forms/medical-history/validation-schema';

interface PastMedicalHistoryProps {
  patientGuid: string;
  icon?: ReactNode;
  sessionId?: string | null;
}

const PastMedicalHistory: FC<PastMedicalHistoryProps> = ({
  patientGuid,
  icon,
  sessionId,
}) => {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { addMedicalHistory, addLoading } = useAddMedicalHistory();
  const { isReadOnlyRole } = useCheckRoleAccess();
  const { showTrackingPlatform, getTrackingPlatform } = useTrackingPlatform({
    patientGuid,
    sessionId,
  });

  const toggleOpen = () => setOpen((prev) => !prev);

  const onSubmit = async ({ record, trackingPlatform }: Values) => {
    if (record)
      addMedicalHistory(
        {
          patientGuid,
          record,
          trackingPlatform: getTrackingPlatform(trackingPlatform),
        },
        { onSuccess: () => setOpen(false) },
      );
  };

  const onCancelForm = () => {
    setOpen(false);
  };

  return (
    <>
      <div className={s.wrapper} id="medical-history">
        <div className={s['title-wrapper']}>
          <h4 className={s.title}>
            {icon ?? <MedicalHistoryIcon />}
            {t('labels.pastMedicalHistory')}
          </h4>
          <BaseButton
            label={`+ ${t('labels.addHistory')}`}
            dataCy="addHistory-button"
            onClick={toggleOpen}
            loading={addLoading}
            disabled={isReadOnlyRole}
          />
        </div>
        <div className={s.divider} />
        <MedicalHistoryTable
          patientGuid={patientGuid}
          page={page}
          setPage={setPage}
          disableActions={addLoading}
        />
      </div>
      {open && (
        <DraggableModal
          title={t('labels.addHistory')}
          isOpen={open}
          toggle={toggleOpen}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <MedicalHistoryForm
            loading={addLoading}
            onCancelForm={onCancelForm}
            onSubmit={onSubmit}
            showTrackingPlatform={showTrackingPlatform}
          />
        </DraggableModal>
      )}
    </>
  );
};
export default PastMedicalHistory;
